import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, MessageCircle, MapPin, Clock3, ChevronRight, ArrowRight, Navigation,
} from "lucide-react";
import { SplitLines, FadeUp, Eyebrow } from "../components/Reveal";
import Faq from "../components/Faq";
import ContactForm from "../components/ContactForm";
import { CtaBanner } from "../components/CtaBanner";
import { SERVICES } from "../data/services";
import { LOCATIONS, getLocation } from "../data/locations";
import { SUB_AREAS } from "../data/subAreas";
import { PHONE_TEL, PHONE_DISPLAY, waLink } from "../data/site";

const subAreaSlugFor = (townSlug, district) => {
  const hit = SUB_AREAS.find((a) => a.town.slug === townSlug && a.district === district);
  return hit ? hit.slug : null;
};

export default function LocationPage() {
  const { slug } = useParams();
  const loc = getLocation(slug);

  useEffect(() => {
    if (loc) document.title = `Mobile Tyre Fitting in ${loc.name} | Motion Tyres 24/7`;
    return () => { document.title = "Motion Tyres | 24/7 Mobile Tyre Fitting — We Come To You"; };
  }, [loc]);

  if (!loc) {
    return (
      <div className="pt-40 pb-24 max-w-7xl mx-auto px-5 sm:px-8" data-testid="location-not-found">
        <h1 className="font-display font-extrabold uppercase text-5xl">Area not found</h1>
        <Link to="/" className="text-[#FF5500] underline mt-4 inline-block">Back to home</Link>
      </div>
    );
  }

  const others = LOCATIONS.filter((l) => l.slug !== slug);
  const waMsg = `Hi Motion Tyres — I need a tyre service in ${loc.name}. Here's my location:`;

  return (
    <div data-testid={`location-page-${slug}`}>
      {/* HERO */}
      <section className="relative min-h-[78vh] flex items-end grain overflow-hidden" data-testid="location-hero">
        <motion.img
          src={loc.image}
          alt={`Motion Tyres mobile tyre fitting in ${loc.name}`}
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.08, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/55 to-[#0B0B0C]/70" />
        <div className="relative w-full max-w-7xl mx-auto px-5 sm:px-8 pb-14 sm:pb-20 pt-40">
          <motion.nav
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.2em] text-zinc-400 mb-6"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#FF5500]">{loc.name}</span>
          </motion.nav>

          <Eyebrow>Coverage area — 24/7</Eyebrow>
          <h1 className="font-display font-extrabold uppercase leading-[0.9] text-6xl sm:text-8xl mt-4">
            <SplitLines lines={[loc.name + "."]} delay={0.3} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-4 text-zinc-300 text-lg"
          >
            {loc.region} · {loc.postcodes} · {loc.routes}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.6 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <span className="flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.15em] text-black bg-[#FF5500] px-4 py-2.5 rounded-full" data-testid="location-eta-badge">
              <Clock3 className="w-4 h-4" /> {loc.eta} typical response
            </span>
            <a
              href={PHONE_TEL}
              data-testid="location-hero-call-button"
              className="btn-glow flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black font-telemetry text-sm font-semibold uppercase tracking-[0.12em]"
            >
              <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
            </a>
            <a
              href={waLink(waMsg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-hero-whatsapp-button"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#16A34A] hover:bg-[#1AB055] text-black font-telemetry text-sm font-semibold uppercase tracking-[0.12em] transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp location
            </a>
          </motion.div>
        </div>
      </section>

      {/* DISTRICTS */}
      <section className="border-y border-[#27272A] bg-[#0E0E10]" data-testid="location-districts-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <FadeUp className="flex items-center gap-3 mb-4">
            <MapPin className="w-5 h-5 text-[#FF5500]" />
            <h2 className="font-display font-extrabold uppercase text-3xl sm:text-4xl">
              Streets and districts we cover in {loc.name}
            </h2>
          </FadeUp>
          <FadeUp delay={0.05}>
            <p className="text-sm text-zinc-400 mb-8">
              Tap any district to open its own local page with every service, response time and booking form.
            </p>
          </FadeUp>
          <div className="flex flex-wrap gap-2.5">
            {loc.districts.map((d, i) => {
              const chipSlug = subAreaSlugFor(loc.slug, d);
              return (
                <FadeUp key={d} delay={i * 0.03}>
                  <Link
                    to={`/in/${chipSlug}`}
                    data-testid={`district-chip-${slug}-${i}`}
                    className="inline-block border border-[#27272A] hover:border-[#FF5500]/60 hover:text-white transition-colors px-4 py-2 rounded-full text-sm text-zinc-300"
                  >
                    {d}
                  </Link>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* LOCAL CONTENT */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="location-content-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <FadeUp>
              <Eyebrow>Local coverage</Eyebrow>
              <h2 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-3 mb-8 leading-[0.95]">
                Mobile tyre fitting
                <br />across {loc.name}.
              </h2>
            </FadeUp>
            {loc.intro.map((p, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <p className="text-zinc-400 text-base sm:text-lg leading-relaxed mb-6">{p}</p>
              </FadeUp>
            ))}
          </div>
          <div className="lg:col-span-5">
            <FadeUp delay={0.1}>
              <div className="bg-[#131316] border border-[#27272A] p-7">
                <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-[#FF5500] mb-5 flex items-center gap-2">
                  <Navigation className="w-4 h-4" /> Area briefing
                </p>
                <dl className="space-y-4">
                  {[
                    ["Typical response", loc.eta],
                    ["Postcodes", loc.postcodes],
                    ["Main routes", loc.routes],
                    ["Availability", "24 hours, 7 days"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-start justify-between gap-4 border-b border-[#27272A] pb-4 last:pb-0 last:border-0">
                      <dt className="font-telemetry text-[10px] uppercase tracking-[0.2em] text-zinc-500 pt-1">{k}</dt>
                      <dd className="font-display font-bold uppercase text-lg text-white text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* SERVICES IN AREA */}
      <section className="border-y border-[#27272A] bg-[#0E0E10]" data-testid="location-services-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <FadeUp>
            <Eyebrow>Available now in {loc.name}</Eyebrow>
            <h2 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-3 mb-12">
              Every service, on wheels.
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.slug} delay={i * 0.05}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`location-service-link-${s.slug}`}
                  className="group flex items-center justify-between gap-4 bg-[#131316] border border-[#27272A] hover:border-[#FF5500]/60 transition-colors px-6 py-5"
                >
                  <div>
                    <h3 className="font-display font-extrabold uppercase text-xl text-white">{s.shortName}</h3>
                    <p className="text-xs text-zinc-500 mt-1 font-telemetry uppercase tracking-[0.15em]">{loc.eta} · {loc.name}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#FF5500] transition-transform duration-300 group-hover:translate-x-1 shrink-0" />
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="location-faq-section">
        <FadeUp>
          <Eyebrow>{loc.name} questions</Eyebrow>
          <h2 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-3 mb-10">
            Local answers.
          </h2>
        </FadeUp>
        <Faq items={loc.faqs} prefix={`loc-${slug}`} />
      </section>

      {/* OTHER AREAS */}
      <section className="border-t border-[#27272A] bg-[#0E0E10]" data-testid="location-others-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
          <FadeUp>
            <p className="font-telemetry text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-6">
              Other areas we cover
            </p>
            <div className="flex flex-wrap gap-3">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/areas/${o.slug}`}
                  data-testid={`location-link-${o.slug}`}
                  className="group flex items-center gap-2 px-5 py-3 rounded-full border border-[#27272A] hover:border-[#FF5500]/60 transition-colors text-sm text-zinc-300 hover:text-white"
                >
                  {o.name}
                  <span className="font-telemetry text-[10px] text-[#FF5500]">{o.eta}</span>
                </Link>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <CtaBanner
        title={`STRANDED IN ${loc.name.toUpperCase()}?`}
        sub={`A Motion Tyres van covers ${loc.region} 24/7. Call the dispatch line or send your location on WhatsApp — typical response ${loc.eta}.`}
      />

      {/* BOOKING */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="location-booking-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <FadeUp>
            <Eyebrow>Book in {loc.name}</Eyebrow>
            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl mt-3 leading-[0.95]">
              A van, outside
              <br />
              <span className="text-[#FF5500]">your door today.</span>
            </h2>
            <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
              Tap the live-location button and we'll pinpoint your spot in{" "}
              {loc.name} — then your enquiry lands straight on our dispatch
              WhatsApp with the map pin attached.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <ContactForm />
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
